import { Helmet } from '@dr.pogodin/react-helmet'
import { Box, Button, Container, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
    accessEndedCopy,
    showBillingLink
} from '@/features/allauth/pages/account/selfServeRules'
import useAuth from '@/hooks/useAuth'
import gtm from '@/lib/gtm'

/**
 * Shown in place of the product when the active org's billing isn't active
 * (self-serve-org-signup — decision 8 / task 7.7). The component is named for the case that
 * motivated it (a lapsed trial), but `is_billing_active` also goes false for a declined card,
 * a cancelled subscription, or a site admin's manual override — so the copy (accessEndedCopy)
 * is deliberately NOT trial-specific; see that function's comment for why. An admin gets a
 * working link to checkout; a member who cannot pay gets told who to ask, never a button they
 * can't use — that's worse than no button at all.
 */
const TrialExpired = () => {
    const theme = useTheme()
    const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'))
    const { user, logout } = useAuth()
    const canPay = showBillingLink(user?.roles)
    const copy = accessEndedCopy(canPay)

    useEffect(() => {
        gtm.push({ event: 'page_view' })
    }, [])

    return (
        <>
            <Helmet>
                <title>{copy.title}</title>
            </Helmet>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    minHeight: '100%',
                    px: 3,
                    py: '80px'
                }}
            >
                <Container maxWidth="sm">
                    <Typography
                        align="center"
                        color="textPrimary"
                        variant={mobileDevice ? 'h4' : 'h3'}
                        gutterBottom
                    >
                        {copy.title}
                    </Typography>

                    <Typography
                        align="center"
                        color="textSecondary"
                        sx={{ mt: 0.5 }}
                    >
                        {copy.body}
                    </Typography>

                    {copy.ctaLabel && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 4
                            }}
                        >
                            <Button
                                color="primary"
                                component={RouterLink}
                                to="/admin/billing"
                                variant="contained"
                                size="large"
                            >
                                {copy.ctaLabel}
                            </Button>
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 3
                        }}
                    >
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => logout()}
                        >
                            Log out
                        </Link>
                    </Box>
                </Container>
            </Box>
        </>
    )
}

export default TrialExpired
